<script setup lang="ts">
// Charteris Phase 2.5 Task 6: superuser-only Admin tab status card. Consumes
// GET /api/admin/charteris/status (Task 3's CharterisAdminStatusEndpoints —
// 404s for a non-superuser, so this card is only ever mounted by AdminPanel
// for a superuser, full stop — see AdminPanel.vue's isSuperUser guard, not
// ANDed with CHARTERIS visibility). Read-only: the SI
// live-ticker kill switch is flipped via a signal_hub restart, not a PUT
// (docs/runbooks/charteris-fund-dark.md) — this card only shows its state.
import { ref, onMounted } from 'vue'
import { api, type CharterisAdminStatusResponse } from '../../api/client'
import { formatUkTime, siSwitchColour, eventLine, latestNavLine } from './charterisAdminStatusHelpers'

const status = ref<CharterisAdminStatusResponse | null>(null)
const error = ref<string | null>(null)

async function load() {
  try {
    status.value = await api.charterisAdminStatus()
    error.value = null
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'unavailable'
  }
}

onMounted(load)
</script>

<template>
  <div class="cas-card" data-testid="charteris-admin-status-card">
    <div class="cas-card-header">Charteris</div>
    <div class="cas-card-body">
      <div v-if="error" class="cas-error" data-testid="charteris-status-error">
        Charteris status unavailable ({{ error }}).
      </div>
      <div v-else-if="status" class="cas-rows">
        <div class="cas-row">
          <span class="cas-label">SI live ticker</span>
          <span class="cas-value" data-testid="charteris-si-switch" :style="{ color: siSwitchColour(status.siTickerSwitch) }">
            {{ status.siTickerSwitch }}
          </span>
        </div>
        <div class="cas-row">
          <span class="cas-label">Last signal</span>
          <span class="cas-value" data-testid="charteris-signal-at">{{ formatUkTime(status.signalAtUtc) }}</span>
        </div>
        <div class="cas-row">
          <span class="cas-label">Latest NAV</span>
          <span class="cas-value" data-testid="charteris-latest-nav">{{ latestNavLine(status.latestNav) }}</span>
        </div>
        <div class="cas-row">
          <span class="cas-label">Capacity</span>
          <span class="cas-value" data-testid="charteris-capacity">{{ eventLine(status.capacity) }}</span>
        </div>
        <div class="cas-row">
          <span class="cas-label">Levy watch</span>
          <span class="cas-value" data-testid="charteris-levy-watch">{{ eventLine(status.levyWatch) }}</span>
        </div>
        <div class="cas-row">
          <span class="cas-label">NAV capture</span>
          <span class="cas-value" data-testid="charteris-nav-capture-gave-up">{{ eventLine(status.navCaptureGaveUp) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Matches AdminPanel.vue's .card / .card-header / .card-body look — scoped CSS
   does not cascade across components, so this is a deliberate, small copy. */
.cas-card { background: #161b22; border: 1px solid #21262d; border-radius: 8px; overflow: hidden; }
.cas-card-header {
  padding: 0.6rem 1rem; background: #1c2128; border-bottom: 1px solid #21262d;
  font-size: 0.85rem; font-weight: 600; color: #e6edf3; text-transform: uppercase; letter-spacing: 0.5px;
}
.cas-card-body { padding: 0.75rem 1rem; }
.cas-rows { display: flex; flex-direction: column; gap: 0.4rem; }
.cas-row { display: flex; gap: 0.5rem; align-items: baseline; flex-wrap: wrap; font-size: 0.85rem; }
.cas-label { color: #6e7681; min-width: 7rem; }
.cas-value { color: #c9d1d9; font-weight: 600; }
.cas-error {
  padding: 0.4rem 0.75rem; background: rgba(248,81,73,0.1); border: 1px solid #f85149;
  border-radius: 4px; font-size: 0.85rem; color: #f85149;
}
</style>
